import { useMemo, useState } from "react";
import { Link } from "@tanstack/react-router";
import { format, parseISO } from "date-fns";
import { ClipboardCheck, RefreshCw } from "lucide-react";
import { EmptyState } from "@/components/shared/empty-state";
import { ErrorBoundary } from "@/components/shared/error-boundary";
import { PageHeader } from "@/components/shared/page-header";
import { StatusDot } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { NativeSelect } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { QUALITY_STATUSES, fmtQty, labelFor, qualityTone } from "@/features/inventory/constants";
import type { QualityStatus } from "@/features/inventory/types";
import { useQcQueue } from "@/features/quality/api";
import type { QcQueueRow } from "@/features/quality/types";
import { VerifyQualitySheet } from "@/features/quality/verify-sheet";
import { isSupabaseConfigured } from "@/lib/supabase";

export function QualityPage() {
  return (
    <ErrorBoundary title="Quality crashed">
      <QualityInner />
    </ErrorBoundary>
  );
}

function QualityInner() {
  const [filter, setFilter] = useState<"open" | QualityStatus | "all">("open");
  const { data = [], isLoading, error, refetch, isFetching } = useQcQueue(filter);
  const [row, setRow] = useState<QcQueueRow | null>(null);

  const kpis = useMemo(() => {
    const pending = data.filter((r) => r.quality_status === "pending").length;
    const hold = data.filter((r) => r.quality_status === "hold").length;
    return { pending, hold, total: data.length };
  }, [data]);

  if (!isSupabaseConfigured) {
    return (
      <EmptyState
        title="Supabase not configured"
        description="Set VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY to use quality."
      />
    );
  }

  return (
    <div className="space-y-3">
      <PageHeader
        title="Quality"
        description="Verify pending / hold batches from production and receipts"
        actions={
          <Button type="button" variant="outline" size="sm" disabled={isFetching} onClick={() => refetch()}>
            <RefreshCw className={isFetching ? "animate-spin" : ""} />
            Refresh
          </Button>
        }
      />

      <div className="flex flex-wrap items-end gap-2 text-xs">
        <div className="rounded-md border border-border bg-card px-2.5 py-1.5">
          <div className="text-2xs text-muted-foreground">Pending</div>
          <div className="font-semibold tabular-nums">{filter === "open" ? kpis.pending : "—"}</div>
        </div>
        <div className="rounded-md border border-border bg-card px-2.5 py-1.5">
          <div className="text-2xs text-muted-foreground">Hold</div>
          <div className="font-semibold tabular-nums">{filter === "open" ? kpis.hold : "—"}</div>
        </div>
        <NativeSelect
          value={filter}
          onChange={(e) => setFilter(e.target.value as typeof filter)}
          className="w-36"
        >
          <option value="open">Open (pending+hold)</option>
          {QUALITY_STATUSES.map((s) => (
            <option key={s.value} value={s.value}>
              {s.label}
            </option>
          ))}
          <option value="all">All in stock</option>
        </NativeSelect>
      </div>

      {error ? <p className="text-sm text-destructive">{(error as Error).message}</p> : null}

      {!isLoading && data.length === 0 ? (
        <EmptyState
          title="QC queue clear"
          description="Batches with pending or hold quality will appear here after production or goods receipt."
          action={
            <Button asChild size="sm" variant="outline">
              <Link to="/production">Production</Link>
            </Button>
          }
        />
      ) : (
        <Table>
          <TableHeader>
            <TableRow className="hover:bg-transparent">
              <TableHead>Batch</TableHead>
              <TableHead>Material</TableHead>
              <TableHead>Source</TableHead>
              <TableHead>Location</TableHead>
              <TableHead className="text-right">Wt</TableHead>
              <TableHead>Quality</TableHead>
              <TableHead />
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={7} className="py-10 text-center text-muted-foreground">
                  Loading…
                </TableCell>
              </TableRow>
            ) : (
              data.map((r) => (
                <TableRow key={r.id}>
                  <TableCell>
                    <Link
                      to="/inventory/$batchId"
                      params={{ batchId: r.id }}
                      className="font-mono text-xs font-medium text-primary hover:underline"
                    >
                      {r.batch_no}
                    </Link>
                    <div className="text-2xs text-muted-foreground">
                      {format(parseISO(r.received_at), "dd MMM yyyy")}
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="text-xs font-medium">{r.material_name}</div>
                    <div className="text-2xs text-muted-foreground">
                      {[r.grade_name, r.shape_label].filter(Boolean).join(" · ")}
                    </div>
                  </TableCell>
                  <TableCell className="text-xs text-muted-foreground">
                    {r.plan_no ? (
                      <>
                        {r.plan_no}
                        {r.process_name ? ` · ${r.process_name}` : ""}
                      </>
                    ) : r.source === "receipt" ? (
                      "Goods receipt"
                    ) : (
                      "—"
                    )}
                  </TableCell>
                  <TableCell className="text-xs">{r.location_code ?? "—"}</TableCell>
                  <TableCell className="text-right tabular-nums">{fmtQty(r.weight)}</TableCell>
                  <TableCell>
                    <StatusDot tone={qualityTone(r.quality_status)}>
                      {labelFor(QUALITY_STATUSES, r.quality_status)}
                    </StatusDot>
                  </TableCell>
                  <TableCell className="text-right">
                    <Button type="button" size="sm" onClick={() => setRow(r)}>
                      <ClipboardCheck />
                      Verify
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      )}

      <VerifyQualitySheet
        open={Boolean(row)}
        onOpenChange={(o) => {
          if (!o) setRow(null);
        }}
        row={row}
      />
    </div>
  );
}
